import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { exchangePblPasswordlessToken } from '@/api/pbl.api';
import { Button } from '@/components/UI/Button';
import { signIn } from '@/redux/auth/auth.slice';
import { useAppDispatch } from '@/redux/store.hook';
import PATH from '@/router/path';
import { getAuthRefreshDeadline } from '@/utils/authToken';
import { hasPblRole } from '@/utils/pblRole';
import { useTranslation } from 'react-i18next';

const Page = styled.main`
  min-height: calc(100vh - 130px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: ${(p) => p.theme.colors.gray000};
`;

const Card = styled.div`
  width: min(100%, 560px);
  padding: 38px 42px;
  background: ${(p) => p.theme.colors.light};
  border-radius: ${(p) => p.theme.radii.lg};
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 18px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.md};
`;

const Text = styled.p`
  margin: 0 0 22px;
  color: ${(p) => p.theme.colors.gray400};
  line-height: 1.5;
`;

const resolveRedirectPath = (redirectTo?: string | null) => {
  if (!redirectTo) return '';

  try {
    const url = new URL(redirectTo, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    if (redirectTo.startsWith('/')) return redirectTo;
  }

  return redirectTo;
};

const PasswordlessCallback = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  useEffect(() => {
    const errorCode = searchParams.get('error');
    const exchangeToken = searchParams.get('exchange_token');
    const redirectTo = searchParams.get('redirect_to');

    const exchange = async () => {
      if (errorCode === 'expired_or_invalid_link') {
        setError('連結失效，請重新申請或請 OC 重寄');
        return;
      }

      if (!exchangeToken) {
        setError('Missing passwordless exchange token.');
        return;
      }

      try {
        const res = (
          await exchangePblPasswordlessToken({
            exchange_token: exchangeToken,
            redirect_to: redirectTo,
          })
        ).data;

        if (!res.success) {
          setError(res.message || 'Passwordless login failed.');
          return;
        }

        dispatch(
          signIn({
            isLogin: true,
            accessToken: res.data.access_token,
            userData: res.data.user,
            applications: res.data.applications,
            registDone: true,
            refreshTokenTime: getAuthRefreshDeadline(res.data.expires_in),
          })
        );

        const defaultPath = hasPblRole(res.data.user, 'citizen') ? PATH.pbl.citizenCaseCreate : '';
        const nextPath = resolveRedirectPath(res.data.passwordless_redirect_to || redirectTo) || defaultPath;

        if (res.data.user.pbl_citizen_profile?.password_setup_required) {
          navigate(`${PATH.pbl.passwordSetup}${nextPath ? `?redirect_to=${encodeURIComponent(nextPath)}` : ''}`, {
            replace: true,
          });
          return;
        }

        if (nextPath) {
          if (nextPath.startsWith('/')) {
            navigate(nextPath, { replace: true });
            return;
          }

          window.location.href = nextPath;
          return;
        }

        if (hasPblRole(res.data.user, 'pbl_lawyer')) {
          navigate(PATH.pbl.lawyerApplicationStatus, { replace: true });
          return;
        }

        navigate(PATH.pbl.cases, { replace: true });
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Passwordless login failed.');
      }
    };

    exchange();
  }, [dispatch, navigate, searchParams]);

  return (
    <Page>
      <Card>
        <Title>
          {error
            ? t('pbl.pagesHardcoded.passwordlessCallback.passwordlessLoginFailed')
            : t('pbl.pagesHardcoded.passwordlessCallback.signingYouIn')}
        </Title>
        <Text>{error || t('pbl.pagesHardcoded.passwordlessCallback.pleaseWaitWhileWeVerifyYourSecureLogin')}</Text>
        {error && (
          <Button size="lg" variant="primary" rounded={false} onClick={() => navigate('/')}>
            {t('pbl.pagesHardcoded.passwordlessCallback.backToHome')}
          </Button>
        )}
      </Card>
    </Page>
  );
};

export default PasswordlessCallback;
